/**
 * Templates curados de Fluxos e Campanhas — sem chamada de IA.
 *
 * Estrutura compatível com o ReactFlow do FlowBuilder.
 * Todos os nodes email_content nascem com `emailId: null` — o usuário (ou
 * o botão "Materializar drafts") cria os drafts reais via
 * `aiEmail.materializeDraftEmails(flowId)`.
 *
 * `kind`:
 *   - "automation": disparo por evento (trigger), o contato entra a qualquer
 *     momento ao satisfazer condição.
 *   - "campaign":   sequência programada com enrollment em massa do início.
 */

export type FlowTemplateNode = {
  id: string;
  type: "email_content" | "delay" | "condition" | "randomizer" | "action" | "end";
  position: { x: number; y: number };
  data: Record<string, unknown>;
};

export type FlowTemplateEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
};

export type FlowTemplate = {
  key: string;
  name: string;
  description: string;
  kind: "automation" | "campaign";
  channel: "email";
  suggestedCopyStyle: "lightcopy" | "agencia" | "launch" | "default";
  category: "Boas-vindas" | "Lançamento" | "Recuperação" | "Retenção" | "Pós-venda" | "Datas";
  estimatedDays: number;
  nodes: FlowTemplateNode[];
  edges: FlowTemplateEdge[];
};

// ── Helpers ────────────────────────────────────────────────────────────────

let __idCounter = 0;
const nid = (prefix: string) => `${prefix}-${++__idCounter}`;
const eid = (a: string, b: string) => `e-${a}-${b}`;

function emailNode(label: string, subject: string, bodyPreview: string, x: number, y: number): FlowTemplateNode {
  const id = nid("node");
  return {
    id,
    type: "email_content",
    position: { x, y },
    data: { label, emailSubject: subject, emailBodyPreview: bodyPreview, emailId: null, emailStatus: "draft" },
  };
}

function delayNode(label: string, days: number, x: number, y: number): FlowTemplateNode {
  return {
    id: nid("node"),
    type: "delay",
    position: { x, y },
    data: { label, days, hours: 0, duration: days, unit: "days" },
  };
}

function endNode(x: number, y: number): FlowTemplateNode {
  return { id: nid("node"), type: "end", position: { x, y }, data: { label: "Fim do fluxo" } };
}

function chain(nodes: FlowTemplateNode[]): FlowTemplateEdge[] {
  const out: FlowTemplateEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    out.push({ id: eid(nodes[i].id, nodes[i + 1].id), source: nodes[i].id, target: nodes[i + 1].id });
  }
  return out;
}

// ── 1. Lançamento (Jeff Walker) ─────────────────────────────────────────────

function buildLaunchTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    emailNode("Pré-pré: convite", "Tô preparando uma coisa", "Pesquisa rápida + curiosidade. Não revela o produto.", x, y += step),
    delayNode("Aguardar 3 dias", 3, x, y += step),
    emailNode("PLC 1 — A Oportunidade", "Pra onde estamos indo", "Mostra a transformação possível. Foco no 'porquê'.", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("PLC 2 — A Transformação", "Como funciona, na prática", "Entrega valor real. Mecânica do método/produto.", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("PLC 3 — A Experiência", "Como é ter o resultado", "Antecipa a oferta. Deixa o cliente se imaginando dentro.", x, y += step),
    delayNode("Aguardar 1 dia (carrinho abre)", 1, x, y += step),
    emailNode("Lançamento — Abertura", "As vagas estão abertas", "Carrinho aberto + escassez de início (preço/vagas).", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Lançamento — Objeções", "Sobre aquela dúvida que você tem", "Quebra de 1-2 objeções específicas (preço, tempo, pra mim).", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Lançamento — Prova", "Quem entrou já está colhendo", "Casos, depoimentos, prova social.", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Lançamento — Última chamada", "Carrinho fecha hoje", "Escassez real (X horas) + lembrete final.", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Pós — para quem não comprou", "O que aconteceu por aqui", "Agradecimento + abertura pra próxima rodada.", x, y += step),
    endNode(x, y += step),
  ];
  return {
    key: "launch_jeff_walker",
    name: "Lançamento (Jeff Walker)",
    description: "Pré-pré, 3 PLCs, 5 dias de carrinho aberto e e-mail pós para quem não comprou.",
    kind: "campaign",
    channel: "email",
    suggestedCopyStyle: "launch",
    category: "Lançamento",
    estimatedDays: 21,
    nodes,
    edges: chain(nodes),
  };
}

// ── 2. Boas-vindas ──────────────────────────────────────────────────────────

function buildWelcomeTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    emailNode("Boas-vindas", "Que bom ter você aqui", "Mensagem curta de recepção + o que esperar dos próximos e-mails.", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Quem somos", "Por trás dos bastidores", "Conta a história/origem da marca de forma honesta.", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("Conteúdo de valor", "Uma coisa que pode te ajudar agora", "Entrega 1 dica/ferramenta concreta. Sem pedir nada.", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("Recursos", "O que você pode fazer aqui", "Apresenta produtos/conteúdos disponíveis sem vender forte.", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("Convite suave", "Quer dar o próximo passo?", "CTA único e claro pro produto principal.", x, y += step),
    endNode(x, y += step),
  ];
  return {
    key: "welcome_series",
    name: "Boas-vindas (5 e-mails)",
    description: "Recepção, quem somos, valor, recursos, convite. Sem agressividade.",
    kind: "automation",
    channel: "email",
    suggestedCopyStyle: "lightcopy",
    category: "Boas-vindas",
    estimatedDays: 7,
    nodes,
    edges: chain(nodes),
  };
}

// ── 3. Carrinho abandonado ──────────────────────────────────────────────────

function buildAbandonedCartTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    delayNode("Aguardar 1 hora", 0, x, y += step),
    emailNode("Carrinho — lembrete", "Você esqueceu algo", "Lembrete leve: o produto ainda está aí.", x, y += step),
    delayNode("Aguardar 1 dia", 1, x, y += step),
    emailNode("Carrinho — objeções", "Posso ajudar com alguma dúvida?", "Antecipa objeções (frete, prazo, garantia).", x, y += step),
    delayNode("Aguardar 2 dias", 2, x, y += step),
    emailNode("Carrinho — última chamada", "Estoque pode acabar", "Última tentativa, sem desconto agressivo. Tom honesto.", x, y += step),
    endNode(x, y += step),
  ];
  // Ajusta primeiro delay para 1 hora (não dias)
  nodes[0].data = { ...nodes[0].data, days: 0, hours: 1, duration: 1, unit: "hours", label: "Aguardar 1 hora" };
  return {
    key: "abandoned_cart",
    name: "Carrinho abandonado (3 e-mails)",
    description: "1h, 24h, 72h após o abandono. Tom honesto, sem desespero.",
    kind: "automation",
    channel: "email",
    suggestedCopyStyle: "lightcopy",
    category: "Recuperação",
    estimatedDays: 3,
    nodes,
    edges: chain(nodes),
  };
}

// ── 4. Pós-compra ───────────────────────────────────────────────────────────

function buildPostPurchaseTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    emailNode("Pedido confirmado", "Tudo certo por aqui", "Confirmação clara + próximos passos.", x, y += step),
    delayNode("Aguardar 3 dias", 3, x, y += step),
    emailNode("Como começar", "Pra você tirar o melhor proveito", "Tutorial / primeiros passos / link de suporte.", x, y += step),
    delayNode("Aguardar 7 dias", 7, x, y += step),
    emailNode("Como está sendo?", "Quero saber sua experiência", "Pede feedback + sinaliza canal de suporte.", x, y += step),
    delayNode("Aguardar 14 dias", 14, x, y += step),
    emailNode("Cross-sell", "Outra coisa que pode te servir", "Sugere produto complementar de forma sutil.", x, y += step),
    endNode(x, y += step),
  ];
  return {
    key: "post_purchase",
    name: "Pós-compra (4 e-mails)",
    description: "Confirmação, onboarding, feedback, cross-sell. Foco em retenção, não em venda.",
    kind: "automation",
    channel: "email",
    suggestedCopyStyle: "lightcopy",
    category: "Pós-venda",
    estimatedDays: 24,
    nodes,
    edges: chain(nodes),
  };
}

// ── 5. Aniversário ──────────────────────────────────────────────────────────

function buildBirthdayTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    emailNode("Feliz aniversário", "Hoje é seu dia", "Mensagem curta + (opcional) cupom de aniversário.", x, y += step),
    endNode(x, y += step),
  ];
  return {
    key: "birthday",
    name: "Aniversário",
    description: "1 e-mail no dia do aniversário do contato. Trigger por data.",
    kind: "automation",
    channel: "email",
    suggestedCopyStyle: "lightcopy",
    category: "Datas",
    estimatedDays: 1,
    nodes,
    edges: chain(nodes),
  };
}

// ── 6. Reengajamento ────────────────────────────────────────────────────────

function buildReengagementTemplate(): FlowTemplate {
  __idCounter = 0;
  const x = 250;
  let y = 0;
  const step = 130;
  const nodes: FlowTemplateNode[] = [
    emailNode("Faz tempo", "A gente sentiu sua falta", "Pergunta direta: o que mudou? Sem culpa.", x, y += step),
    delayNode("Aguardar 5 dias", 5, x, y += step),
    emailNode("Conteúdo de valor", "Um conteúdo pra você voltar", "Entrega algo útil, sem pedir nada de volta.", x, y += step),
    delayNode("Aguardar 7 dias", 7, x, y += step),
    emailNode("Última tentativa", "Continuar te enviando e-mails?", "Pergunta honestamente se quer continuar — link pra confirmar/sair.", x, y += step),
    endNode(x, y += step),
  ];
  return {
    key: "reengagement",
    name: "Reengajamento (3 e-mails)",
    description: "Tom franco para contatos inativos. Termina com pergunta de permissão.",
    kind: "automation",
    channel: "email",
    suggestedCopyStyle: "lightcopy",
    category: "Retenção",
    estimatedDays: 14,
    nodes,
    edges: chain(nodes),
  };
}

// ── Export ──────────────────────────────────────────────────────────────────

export const FLOW_TEMPLATES: FlowTemplate[] = [
  buildLaunchTemplate(),
  buildWelcomeTemplate(),
  buildAbandonedCartTemplate(),
  buildPostPurchaseTemplate(),
  buildBirthdayTemplate(),
  buildReengagementTemplate(),
];

export const TEMPLATES_BY_KIND = {
  campaign: FLOW_TEMPLATES.filter((t) => t.kind === "campaign"),
  automation: FLOW_TEMPLATES.filter((t) => t.kind === "automation"),
};

export function getTemplateByKey(key: string): FlowTemplate | undefined {
  return FLOW_TEMPLATES.find((t) => t.key === key);
}
